import { Box } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useLyricsTheme } from '../context/LyricsThemeContext';

interface AlbumGradientProps {
  imageUrl: string;
}

/**
 * AlbumGradient creates a dynamic background gradient based on an album artwork
 */
const AlbumGradient = ({ imageUrl }: AlbumGradientProps) => {
  const [gradient, setGradient] = useState<string | null>(null);
  const { setDominantColor, backgroundType, backgroundColor, isDarkMode } = useLyricsTheme();

  useEffect(() => {
    const extractColors = async () => {
      if (!imageUrl) return;

      try {
        // Load the image data
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = imageUrl;
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // Create a canvas to sample colors
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        // Set canvas dimensions to a small size for sampling
        canvas.width = 50;
        canvas.height = 50;
        
        // Draw the image to canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Get the pixel data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        
        // Calculate average color
        let r = 0, g = 0, b = 0;
        for (let i = 0; i < imageData.length; i += 4) {
          r += imageData[i];
          g += imageData[i + 1];
          b += imageData[i + 2];
        }
        
        const pixelCount = imageData.length / 4;
        r = Math.floor(r / pixelCount);
        g = Math.floor(g / pixelCount);
        b = Math.floor(b / pixelCount);
        
        // Create a darker version for gradient
        const darkR = Math.floor(r * 0.6);
        const darkG = Math.floor(g * 0.6);
        const darkB = Math.floor(b * 0.6);
        
        // Save dominant color
        const dominantColor = `rgb(${r}, ${g}, ${b})`;
        setDominantColor(dominantColor);
        
        // Create gradient
        const gradientCSS = `linear-gradient(135deg, rgba(${darkR}, ${darkG}, ${darkB}, 0.95) 0%, rgba(${r}, ${g}, ${b}, 0.7) 100%)`;
        setGradient(gradientCSS);
        
      } catch (error) {
        console.error('Error extracting colors:', error);
        setGradient(null);
      }
    };
    
    extractColors();
    
    return () => {
      // Cleanup
      setGradient(null);
    };
  }, [imageUrl, setDominantColor]);

  // If not using gradient background or no image URL is provided, don't render
  if (backgroundType === 'solid' || !imageUrl) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg={gradient || 'rgba(0, 0, 0, 0.9)'}
      zIndex={0}
      height="100vh"
      width="100vw"
      transition="background 0.5s ease"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: gradient 
          ? `linear-gradient(to bottom, transparent 0%, ${isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.7)'} 100%)`
          : isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.7)',
        zIndex: 1
      }}
    />
  );
};

export default AlbumGradient; 